import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BsCheck2, BsX, BsSearch } from "react-icons/bs";
import Handler from "../../../Helpers/Handler";
import { useAuth } from "../../../Contexts/AuthenticateProvider";
import { formatDate, formatDateTime, textCapitalize } from "../../../Helpers/Utils";
import TablePlaceHolder from "../../../Components/TablePlaceHolder";
import useListParams from "../../../Helpers/useListParams";
import { LeaveCancelModal, CancelRequestChip, LEAVE_STATUS_CLASS, cancellableDays, hasWaitingCancelRequest, leaveTypeText } from "../../../Components/LeaveCancel";

const TYPES = [
    { value: "", label: "All types" },
    { value: "CASUAL_LEAVE", label: "Casual leave" },
    { value: "SICK_LEAVE", label: "Sick leave" },
    { value: "EMERGENCY_LEAVE", label: "Emergency leave" },
    { value: "HALF_DAY", label: "Half day" },
];

const STATUS_CLASS = LEAVE_STATUS_CLASS;
// "Cancel requests" is not a status of its own: it is approved leave with a cancel request waiting.
const CANCEL_REQUESTS = "CANCEL_REQUESTS";
const COUNT = 10;

const LeaveRequests = () => {
    const { hasPermission } = useAuth();
    const canDecide = hasPermission("staff.timeoff.decide");
    const [list, setList] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(true);
    const [deciding, setDeciding] = useState("");
    const [cancelRequestCount, setCancelRequestCount] = useState(0);
    // The leave a supervisor is cancelling directly.
    const [cancelTarget, setCancelTarget] = useState(null);
    // Page number in the URL path, filters in the query string (next to the page's ?tab=) - see Helpers/useListParams.
    const { page, setPage, filters: filter, setFilters } = useListParams({ status: "", type: "", search: "" }, { zeroBased: true });
    const setFilter = (next) => { setFilters(next); setPage(0); };
    const [searchDraft, setSearchDraft] = useState(filter.search);

    const load = useCallback(async (pageNo) => {
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: `/staff/team/leave/list/${pageNo}`,
            params: { count: COUNT },
            data: {
                ...(filter.status === CANCEL_REQUESTS ? { status: "APPROVED", cancelPending: true } : filter.status ? { status: filter.status } : {}),
                ...(filter.type ? { type: filter.type } : {}),
                ...(filter.search ? { search: filter.search } : {}),
            },
        });
        if (response?.success && response?.data?.success) {
            const data = response?.data?.data || {};
            setList(data.list || []);
            setPendingCount(data.pendingCount || 0);
            setCancelRequestCount(data.cancelRequestCount || 0);
            setHasNext(Boolean(data.next));
        } else {
            setList([]);
            setPendingCount(0);
            setCancelRequestCount(0);
            setHasNext(false);
        }
        setLoading(false);
    }, [filter]);

    useEffect(() => { load(page); }, [load, page]);

    const decide = async (leaveId, decision) => {
        setDeciding(leaveId);
        const response = await Handler({ method: "patch", url: `/staff/team/leave/${leaveId}/decision`, data: { decision } });
        if (response?.success && response?.data?.success) {
            toast.success(response?.data?.message || "Decision recorded");
            await load(page);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Could not record the decision"));
        }
        setDeciding("");
    };

    const decideCancel = async (leaveId, decision) => {
        setDeciding(leaveId);
        const response = await Handler({ method: "patch", url: `/staff/team/leave/${leaveId}/cancel-decision`, data: { decision } });
        if (response?.success && response?.data?.success) {
            toast.success(response?.data?.message || "Decision recorded");
            await load(page);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Could not record the decision"));
        }
        setDeciding("");
    };

    return (
        <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <h6 className="fw-semibold mb-0">
                        Leave requests
                        {pendingCount ? <span className="att-chip att-warn ms-2">{pendingCount} pending</span> : null}
                        {cancelRequestCount ? <span className="att-chip att-bad ms-2">{cancelRequestCount} cancel {cancelRequestCount === 1 ? "request" : "requests"}</span> : null}
                    </h6>
                    <div className="d-flex gap-2 flex-wrap flex-row">
                        {/* <div className="input-group input-group-sm att-search-group">
                            <span className="input-group-text"><BsSearch /></span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Name or staff ID"
                                value={searchDraft}
                                onChange={(event) => setSearchDraft(event.target.value)}
                                onKeyDown={(event) => { if (event.key === "Enter") setFilter((prev) => ({ ...prev, search: searchDraft.trim() })); }}
                            />
                        </div> */}
                        <div> <select
                            className="form-select form-select-sm att-filter-select"
                            value={filter.type}
                            onChange={(event) => setFilter((prev) => ({ ...prev, type: event.target.value }))}
                            aria-label="Filter by type"
                        >
                            {TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                        </select></div>
                        <div> <select
                            className="form-select form-select-sm att-filter-select"
                            value={filter.status}
                            onChange={(event) => setFilter((prev) => ({ ...prev, status: event.target.value }))}
                            aria-label="Filter by status"
                        >
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="CANCELLED">Cancelled</option>
                            <option value={CANCEL_REQUESTS}>Cancel requests</option>
                            <option value="">All statuses</option>
                        </select></div>
                    </div>
                </div>

                <div className={`list-table-wrap`}>
                    <div className='table-responsive list-table-scroll'>
                        <table className="table align-middle mb-0 list-table">
                            <thead className='sticky-top'>
                                <tr className="border-bottom">
                                    {/* <th className="p-1">Request</th> */}
                                    <th className="py-3 px-3 list-th">Staff</th>
                                    <th className="py-3 px-3 list-th">Type</th>
                                    <th className="py-3 px-3 list-th">Dates</th>
                                    <th className="py-3 px-3 list-th">Reason</th>
                                    <th className="py-3 px-3 list-th">Status</th>
                                    <th className="py-3 px-3 list-th">Applied</th>
                                    <th className="py-3 px-3 list-th">Decision</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? <TablePlaceHolder count={7} /> : list.length ? list.map((row) => (
                                    <tr key={row.leaveId}>
                                        {/* <td className="p-1 fw-semibold">{row.leaveId}</td> */}
                                        <td className="p-1">
                                            <div>{row.staffName || row.staffId}</div>
                                            <div className="text-muted small">{row.employeeId || row.staffId}</div>
                                        </td>
                                        <td className="p-1">{leaveTypeText(row)}</td>
                                        <td className="p-1">{row.type === "HALF_DAY" ? formatDate(row.fromDate) : <>{formatDate(row.fromDate)} → {formatDate(row.toDate)}</>}</td>
                                        <td className="p-1 att-reason-cell" title={row.reason}>
                                            {row.reason || "-"}
                                            {row.status === "CANCELLED" && row.cancelReason ? <div className="text-muted small">Cancelled: {row.cancelReason}</div> : null}
                                            {hasWaitingCancelRequest(row) ? (
                                                <div className="small mt-1">
                                                    <span className="fw-semibold">Cancel request</span> ({formatDate(row.cancelRequest.fromDate)}{row.cancelRequest.fromDate !== row.cancelRequest.toDate ? ` → ${formatDate(row.cancelRequest.toDate)}` : ""}): {row.cancelRequest.reason}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td className="p-1">
                                            <span className={`att-chip ${STATUS_CLASS[row.status] || "att-muted"}`}>{textCapitalize(row.status)}</span>
                                            <CancelRequestChip row={row} />
                                        </td>
                                        <td className="p-1 text-muted">{formatDateTime(row.createdAt)}</td>
                                        <td className="p-1">
                                            {row.status === "PENDING" && canDecide ? (
                                                <div className="d-inline-flex gap-1">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-success"
                                                        disabled={Boolean(deciding)}
                                                        onClick={() => decide(row.leaveId, "APPROVED")}
                                                    >
                                                        <BsCheck2 /> Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        disabled={Boolean(deciding)}
                                                        onClick={() => decide(row.leaveId, "REJECTED")}
                                                    >
                                                        <BsX /> Reject
                                                    </button>
                                                </div>
                                            ) : hasWaitingCancelRequest(row) && canDecide ? (
                                                <div className="d-inline-flex gap-1">
                                                    <button type="button" className="btn btn-sm btn-success" disabled={Boolean(deciding)} onClick={() => decideCancel(row.leaveId, "APPROVED")}>
                                                        <BsCheck2 /> Approve cancel
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={Boolean(deciding)} onClick={() => decideCancel(row.leaveId, "REJECTED")}>
                                                        <BsX /> Keep leave
                                                    </button>
                                                </div>
                                            ) : canDecide && (row.status === "PENDING" || (row.status === "APPROVED" && cancellableDays(row))) ? (
                                                <div className="d-flex flex-column align-items-start gap-1">
                                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setCancelTarget(row)}>Cancel leave</button>
                                                    <span className="text-muted small">{row.decidedBy || ""}</span>
                                                </div>
                                            ) : (
                                                <span className="text-muted small">
                                                    {row.status === "PENDING" ? "Awaiting a decision" : (row.decidedBy || "-")}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={8} className="text-center p-3 text-muted">No leave requests match these filters.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="d-flex justify-content-between mt-3">
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0 || loading} onClick={() => setPage(page - 1)}>Previous</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!hasNext || loading} onClick={() => setPage(page + 1)}>Next</button>
                </div>
            </div>
            {cancelTarget ? (
                <LeaveCancelModal
                    leave={cancelTarget}
                    mode={cancelTarget.status === "PENDING" ? "withdraw" : "direct"}
                    url={`/staff/team/leave/${cancelTarget.leaveId}/cancel`}
                    onClose={() => setCancelTarget(null)}
                    onDone={() => { setCancelTarget(null); load(page); }}
                />
            ) : null}
        </div>
    );
};

export default LeaveRequests;
